import type { CustomersSummaryDocumentFilterInput } from "~/gql/graphqlBase.generated";

const PHONE_LIKE_TERM_PATTERN = /^[\d\s().+-]+$/;
const MIN_PHONE_DIGITS = 2;

export interface ParsedCustomerSearchTerm {
	phoneDigits: string | null;
	text: string;
}

export interface CustomerSearchContactEntry {
	contactId: string;
	name: string;
	phone: string;
	phoneDigits: string;
}

export interface CustomerSearchMatchNode {
	contactSearchEntries?: readonly CustomerSearchContactEntry[] | null;
	name: string;
}

export interface CustomerSearchMatch {
	kind: "contact" | "phone";
	text: string;
}

export const parseCustomerSearchTerm = (term: string): ParsedCustomerSearchTerm => {
	const text = term.trim();
	const digits = text.replace(/\D/g, "");
	const phoneDigits = PHONE_LIKE_TERM_PATTERN.test(text) && digits.length >= MIN_PHONE_DIGITS ? digits : null;
	return { phoneDigits, text };
};

export const buildCustomerSearchOr = (term: string): CustomersSummaryDocumentFilterInput[] => {
	const { phoneDigits, text } = parseCustomerSearchTerm(term);
	const clauses: CustomersSummaryDocumentFilterInput[] = [
		{ name: { contains: text } },
		{ erpId: { contains: text } },
		{ customerLookupCode: { contains: text } },
		{ contactSearchEntries: { some: { name: { contains: text } } } }
	];
	if (phoneDigits) {
		clauses.push({ contactSearchEntries: { some: { phoneDigits: { contains: phoneDigits } } } });
	}
	return clauses;
};

export const describeCustomerMatch = (node: CustomerSearchMatchNode, term: string): CustomerSearchMatch | null => {
	const { phoneDigits, text } = parseCustomerSearchTerm(term);
	const entries = node.contactSearchEntries ?? [];

	if (phoneDigits) {
		const phoneMatch = entries.find((entry) => entry.phoneDigits.includes(phoneDigits));
		if (phoneMatch) {
			return { kind: "phone", text: phoneMatch.name ? `${phoneMatch.phone} · ${phoneMatch.name}` : phoneMatch.phone };
		}
	}

	if (text && !node.name.toLowerCase().includes(text.toLowerCase())) {
		const contactMatch = entries.find((entry) => entry.name.toLowerCase().includes(text.toLowerCase()));
		if (contactMatch) {
			return { kind: "contact", text: contactMatch.name };
		}
	}

	return null;
};
